import React, { createContext, useContext } from "react";
import { Box, CircularProgress, Sheet, Typography } from "@mui/joy";
import { useTheme } from "@mui/joy/styles";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import type { CanvasDataSource } from "../../../types";
import {
  useViewAggregateQuery,
  type AggregateRequest,
} from "../../../hooks/useViewAggregateQuery";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

export const CanvasWidgetDataContext = createContext<CanvasDataSource[] | null>(
  null,
);

type WidgetConfig = {
  widget_type?: "CHART" | "METRIC" | "TABLE";
  data_source_alias?: string;
  title?: string;
  chart_config?: {
    chart_type?: string;
    x_field?: string;
    series_field?: string;
    y_fields?: string[];
    aggregation?: string;
    /** Optional chart text sizes in pixels, between 8 and 24. */
    legend_font_size?: number;
    axis_font_size?: number;
  };
  metric_config?: {
    value_field?: string;
    aggregation?: string;
    format?: string;
  };
  table_config?: {
    columns?: Array<{ field?: string; label?: string; aggregation?: string }>;
  };
};

export function requestFor(config: WidgetConfig): AggregateRequest | null {
  if (config.widget_type === "METRIC") {
    const metric = config.metric_config;
    if (
      !metric?.aggregation ||
      (metric.aggregation !== "count" && !metric.value_field)
    )
      return null;
    return {
      group_by: [],
      measures: [
        {
          key: "value",
          aggregation: metric.aggregation,
          field:
            metric.aggregation === "count" ? undefined : metric.value_field,
        },
      ],
    };
  }
  if (config.widget_type === "CHART") {
    const chart = config.chart_config;
    if (
      !chart?.x_field ||
      !chart.y_fields?.length ||
      chart.y_fields.some((field) => !field)
    )
      return null;
    // The API rejects a duplicate group field, so a series equal to the x field
    // is an unconfigured chart rather than something to drop silently.
    if (chart.series_field && chart.series_field === chart.x_field) return null;
    return {
      group_by: [chart.x_field],
      ...(chart.series_field ? { series_field: chart.series_field } : {}),
      measures: chart.y_fields.map((field, index) => ({
        key: `series_${index}`,
        aggregation: chart.aggregation || "sum",
        field: (chart.aggregation || "sum") === "count" ? undefined : field,
      })),
    };
  }
  if (config.widget_type === "TABLE") {
    const columns = config.table_config?.columns || [];
    const group = columns[0]?.field;
    if (
      !group ||
      columns.length < 2 ||
      columns
        .slice(1)
        .some((column) => column.aggregation !== "count" && !column.field)
    )
      return null;
    return {
      group_by: [group],
      measures: columns.slice(1).map((column, index) => ({
        key: `measure_${index}`,
        aggregation: column.aggregation || "count",
        field: column.aggregation === "count" ? undefined : column.field,
      })),
    };
  }
  return null;
}

export function CanvasAggregateWidget({
  element,
  mode,
}: {
  element: RenderableElement;
  mode: "edit" | "runtime";
}) {
  const theme = useTheme();
  const runtime = useCanvasRuntime();
  const config = element.props as WidgetConfig;
  const sources = useContext(CanvasWidgetDataContext);
  const source = sources?.find(
    (item) =>
      item.id === config.data_source_alias ||
      item.name === config.data_source_alias,
  );
  const request = requestFor(config);
  const query = useViewAggregateQuery(
    source?.view_id?.toString(),
    request,
    mode === "runtime" && !!source,
    mode === "runtime" ? runtime?.canvasId : null,
  );
  const title = config.title || String(element.props.label || "Widget");

  if (mode === "edit")
    return (
      <Sheet variant="soft" sx={{ p: 2, height: "100%" }}>
        <Typography level="title-sm">{title}</Typography>
        <Typography level="body-xs">
          {config.widget_type || "Widget"} preview
        </Typography>
      </Sheet>
    );
  if (!source)
    return (
      <Typography level="body-sm">Data source is not configured.</Typography>
    );
  if (!request)
    return (
      <Typography level="body-sm">Configure the widget fields.</Typography>
    );
  if (query.isLoading)
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size="sm" /> Loading {title}
      </Box>
    );
  if (query.isError)
    return <Typography color="danger">Could not load {title}.</Typography>;
  const points = query.data?.current || [];
  if (!points.length)
    return <Typography level="body-sm">No data for {title}.</Typography>;

  if (config.widget_type === "METRIC") {
    const value = Number(points[0].values.value || 0);
    const format = config.metric_config?.format;
    const formatted = new Intl.NumberFormat(
      undefined,
      format === "currency"
        ? { style: "currency", currency: "USD" }
        : format === "percentage"
          ? { style: "percent" }
          : {},
    ).format(value);
    return (
      <Sheet variant="soft" sx={{ p: 2, height: "100%" }}>
        <Typography level="title-sm">{title}</Typography>
        <Typography level="h2">{formatted}</Typography>
      </Sheet>
    );
  }
  const group = request.group_by[0];
  const labels = points.map((point) => String(point.keys[group] ?? ""));
  const measures = request.measures;
  if (config.widget_type === "TABLE") {
    const columns = config.table_config?.columns || [];
    return (
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Typography level="title-sm">{title}</Typography>
        <table>
          <thead>
            <tr>
              <th>{columns[0]?.label || group}</th>
              {measures.map((measure, index) => (
                <th key={measure.key}>
                  {columns[index + 1]?.label || measure.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {points.map((point, index) => (
              <tr key={index}>
                <td>{labels[index]}</td>
                {measures.map((measure) => (
                  <td key={measure.key}>
                    {new Intl.NumberFormat().format(
                      Number(point.values[measure.key] || 0),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  }
  const palette = [
    theme.palette.primary[500],
    theme.palette.success[500],
    theme.palette.warning[500],
    theme.palette.danger[500],
    theme.palette.neutral[500],
    theme.palette.primary[700],
  ];
  const seriesField = request.series_field;
  const measureLabel = (index: number, key: string) =>
    config.chart_config?.y_fields?.[index] || key;
  // Without a series field: one dataset per measure over the x groups. With one:
  // the API returns a point per (x, series) pair, so pivot it into one dataset per
  // series value (and per measure) over the distinct x values, in response order.
  let chartLabels = labels;
  let datasets: Array<{
    label: string;
    data: Array<number | null>;
    borderColor: string;
    backgroundColor: string;
  }>;
  if (seriesField) {
    const cell = (x: string, series: string) => `${x}\u0000${series}`;
    const xs: string[] = [];
    const seriesValues: string[] = [];
    const byCell = new Map<string, (typeof points)[number]>();
    for (const point of points) {
      const x = String(point.keys[group] ?? "");
      const series = String(point.keys[seriesField] ?? "");
      if (!xs.includes(x)) xs.push(x);
      if (!seriesValues.includes(series)) seriesValues.push(series);
      byCell.set(cell(x, series), point);
    }
    chartLabels = xs;
    datasets = seriesValues.flatMap((series, seriesIndex) =>
      measures.map((measure, measureIndex) => {
        const color =
          palette[
            (seriesIndex * measures.length + measureIndex) % palette.length
          ];
        return {
          label:
            measures.length > 1
              ? `${series} \u00b7 ${measureLabel(measureIndex, measure.key)}`
              : series,
          data: xs.map((x) => {
            const point = byCell.get(cell(x, series));
            return point ? Number(point.values[measure.key] || 0) : null;
          }),
          borderColor: color,
          backgroundColor: color,
        };
      }),
    );
  } else {
    datasets = measures.map((measure, index) => ({
      label: measureLabel(index, measure.key),
      data: points.map((point) => Number(point.values[measure.key] || 0)),
      borderColor: palette[index % palette.length],
      backgroundColor: palette[index % palette.length],
    }));
  }
  const chartData = { labels: chartLabels, datasets };
  const boundedFontSize = (value: unknown) =>
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 8 &&
    value <= 24
      ? value
      : undefined;
  const legendFontSize = boundedFontSize(config.chart_config?.legend_font_size);
  const axisFontSize = boundedFontSize(config.chart_config?.axis_font_size);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: datasets.length > 1,
        ...(legendFontSize
          ? { labels: { font: { size: legendFontSize } } }
          : {}),
      },
    },
    ...(axisFontSize
      ? {
          scales: {
            x: { ticks: { font: { size: axisFontSize } } },
            y: { ticks: { font: { size: axisFontSize } } },
          },
        }
      : {}),
  };
  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 180 }}>
      <Typography level="title-sm">{title}</Typography>
      <Box sx={{ height: "calc(100% - 28px)" }}>
        {config.chart_config?.chart_type === "line" ? (
          <Line data={chartData} options={options} />
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </Box>
    </Box>
  );
}
